import config from '../config';
import { v4 } from 'uuid';

type AnalyticsCookie = {
  loggedInGuid: string;
  loggedOutGuid: string;
};

export function getAnalyticsCookie(): AnalyticsCookie {
  const [, ...values] =
    document.cookie
      .split('; ')
      .find((x) => x.startsWith(`${config.analyticsCookieName}=`))
      ?.split('=') ?? [];

  const analyticsCookieValue = new URLSearchParams(values.join('='));

  return {
    loggedInGuid: analyticsCookieValue.get('logged_in_guid') ?? '',
    loggedOutGuid: analyticsCookieValue.get('logged_out_guid') ?? '',
  };
}

export function createAnalyticsCookie(loggedInGuid?: string) {
  const cookieValue = new URLSearchParams();
  cookieValue.set('logged_in_guid', '00000000-0000-0000-0000-000000000000');
  cookieValue.set('logged_out_guid', v4());
  cookieValue.set('source', 'JG.Pages.Creation');

  if (loggedInGuid) {
    cookieValue.set('logged_in_guid', loggedInGuid);
  }

  const domain = 'domain=.justgiving.com;';
  const path = `path=/;`;
  const maxAge = `max-age=${60 * 60 * 24 * 365 * 10};`; // 10 Years

  document.cookie = `${
    config.analyticsCookieName
  }=${cookieValue.toString()};${domain}${path}${maxAge}`;
}

export function analyticsCookieExists() {
  return document.cookie.includes(`${config.analyticsCookieName}=`);
}

/**
 * Re-create the JGAnalytics cookie only when it is missing, leaving an existing
 * cookie (and its guid) untouched.
 */
export function ensureAnalyticsCookie(loggedInGuid?: string) {
  if (!analyticsCookieExists()) {
    createAnalyticsCookie(loggedInGuid);
  }
}

/**
 * TEMPORARY: Cookiebot deletes JGAnalytics when a user chooses "necessary
 * cookies only", because the live Cookiebot config still classifies it as a
 * Statistics cookie rather than Necessary (and it is not in the whitelist).
 *
 * `CookiebotOnConsentReady` fires on every page load once the consent state is
 * known, and again after the user submits a choice. Re-asserting the cookie
 * here restores it on the next page load (e.g. the SSO login page), where
 * Cookiebot does not re-purge it — so it is present by the time Optimizely
 * enrols the user at SSO.
 *
 * Remove once Cookiebot classifies JGAnalytics as Necessary and whitelists it.
 */
export function registerAnalyticsCookieReassert() {
  window.addEventListener('CookiebotOnConsentReady', () => {
    ensureAnalyticsCookie();
  });
}
